import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, CreditCard, AlertTriangle } from 'lucide-react';
import {
  nexiService,
  messaggioEsito,
  STATI_CONCLUSI_KO,
  STATI_CONCLUSI_OK,
  type Pagamento,
} from '../../services/nexiService';
import { useCart } from '../../context/CartContext';

const MAX_TENTATIVI = 20;
const INTERVALLO_MS = 2000;

/**
 * Esito del pagamento.
 *
 * Interroga il backend finché lo stato non è definitivo: la notifica
 * server-to-server di Nexi può arrivare dopo il rientro del browser, e il
 * backend recupera comunque l'esito in pull. Questa pagina **mostra** lo
 * stato, non lo decide — nessuna chiamata da qui conferma un ordine.
 */
export default function PaymentResultPage() {
  const [params] = useSearchParams();
  const codTrans = params.get('codTrans') || '';
  const erroreFirma = params.get('stato') === 'errore';

  const [pagamento, setPagamento] = useState<Pagamento | null>(null);
  const [esaurito, setEsaurito] = useState(false);
  const { clearCart } = useCart();
  const carrelloSvuotato = useRef(false);

  useEffect(() => {
    if (!codTrans) return;
    let attivo = true;
    let timer: ReturnType<typeof setTimeout>;

    const interroga = async (tentativo: number) => {
      try {
        const risultato = await nexiService.stato(codTrans);
        if (!attivo) return;
        setPagamento(risultato);

        if (STATI_CONCLUSI_OK.includes(risultato.stato)) {
          if (!carrelloSvuotato.current) {
            carrelloSvuotato.current = true;
            clearCart();
          }
          return;
        }
        if (STATI_CONCLUSI_KO.includes(risultato.stato)) return;
      } catch {
        /* riprova: il backend può star recuperando l'esito dal gateway */
      }
      if (!attivo) return;
      if (tentativo >= MAX_TENTATIVI) {
        setEsaurito(true);
        return;
      }
      timer = setTimeout(() => interroga(tentativo + 1), INTERVALLO_MS);
    };

    void interroga(0);
    return () => {
      attivo = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codTrans]);

  const pagato = pagamento != null && STATI_CONCLUSI_OK.includes(pagamento.stato);
  const fallito = pagamento != null && STATI_CONCLUSI_KO.includes(pagamento.stato);
  const inCorso = !pagato && !fallito && !esaurito && !erroreFirma;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12" data-testid="payment-result-page">
      <div className="rounded-xl border bg-white p-8 text-center">
        {erroreFirma && (
          <div data-testid="payment-signature-error">
            <AlertTriangle className="w-10 h-10 text-amber-600 mx-auto mb-4" />
            <h1 className="text-xl font-semibold mb-2">Esito non verificabile</h1>
            <p className="text-sm text-muted-foreground mb-5">
              Non siamo riusciti a verificare la risposta del gateway. Se hai completato il
              pagamento controlla i tuoi ordini fra qualche minuto prima di riprovare:
              nessun ordine viene confermato senza un esito valido.
            </p>
            <Link
              to="/area-clienti/ordini"
              className="h-10 px-5 rounded-lg border text-sm font-medium inline-flex items-center"
            >
              Vedi i miei ordini
            </Link>
          </div>
        )}

        {inCorso && (
          <div data-testid="payment-pending">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-[hsl(214,58%,41%)] mb-4" />
            <h1 className="text-xl font-semibold mb-2">Pagamento in elaborazione</h1>
            <p className="text-sm text-muted-foreground">
              Stiamo confermando l&apos;esito con il circuito e registrando l&apos;ordine.
              Non chiudere questa pagina.
            </p>
          </div>
        )}

        {esaurito && !pagato && !fallito && (
          <div data-testid="payment-timeout">
            <AlertTriangle className="w-10 h-10 text-amber-600 mx-auto mb-4" />
            <h1 className="text-xl font-semibold mb-2">Esito ancora in arrivo</h1>
            <p className="text-sm text-muted-foreground mb-5">
              Il circuito non ha ancora risposto. L&apos;esito viene registrato comunque:
              troverai l&apos;ordine fra i tuoi ordini se il pagamento è andato a buon fine.
              <strong className="block mt-2 font-medium text-foreground">
                Non ripetere il pagamento.
              </strong>
            </p>
            <Link
              to="/area-clienti/ordini"
              className="h-10 px-5 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium inline-flex items-center"
            >
              Vedi i miei ordini
            </Link>
          </div>
        )}

        {pagato && pagamento && (
          <div data-testid="payment-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-4" />
            <h1 className="text-xl font-semibold mb-2">Pagamento riuscito</h1>
            <p className="text-sm text-muted-foreground mb-5">
              Il tuo ordine{' '}
              <span className="font-mono font-medium text-foreground">
                {pagamento.numeroGestionale ?? pagamento.numeroOrdine}
              </span>{' '}
              è stato registrato.
            </p>

            <div
              className="rounded-lg border bg-[hsl(var(--dtk-canvas))] p-4 text-left text-xs space-y-1 mb-5"
              data-testid="payment-receipt"
            >
              <div className="flex justify-between">
                <span className="text-muted-foreground">Importo addebitato</span>
                <span className="font-medium tabular-nums">
                  € {(pagamento.amountCents / 100).toFixed(2).replace('.', ',')}
                </span>
              </div>
              {pagamento.brand && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Carta</span>
                  <span className="font-medium">
                    {pagamento.brand} {pagamento.pan}
                  </span>
                </div>
              )}
              {pagamento.codAut && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Autorizzazione</span>
                  <span className="font-mono">{pagamento.codAut}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Riferimento</span>
                <span className="font-mono">{pagamento.codTrans}</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-2">
              <Link
                to="/area-clienti/ordini"
                className="h-10 px-5 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium inline-flex items-center"
                data-testid="payment-goto-orders"
              >
                Vedi i miei ordini
              </Link>
              <Link
                to="/prodotti/tutti"
                className="h-10 px-5 rounded-lg border text-sm font-medium inline-flex items-center hover:bg-secondary/60"
              >
                Continua gli acquisti
              </Link>
            </div>
          </div>
        )}

        {fallito && pagamento && (
          <div data-testid="payment-failed">
            <XCircle className="w-10 h-10 text-red-600 mx-auto mb-4" />
            <h1 className="text-xl font-semibold mb-2">Pagamento non completato</h1>
            <p className="text-sm text-muted-foreground mb-1">{messaggioEsito(pagamento)}</p>
            <p className="text-xs text-muted-foreground mb-5">
              {pagamento.stato === 'RIMBORSATO'
                ? 'L’importo è stato restituito sulla tua carta.'
                : 'Nessun importo è stato addebitato. Il carrello è ancora disponibile.'}
            </p>
            <Link
              to="/area-clienti/checkout"
              className="h-10 px-5 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium inline-flex items-center gap-2"
              data-testid="payment-retry"
            >
              <CreditCard className="w-4 h-4" /> Torna al checkout
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
